import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight, Menu, X, TrendingUp,
  GitBranch, UserX, ShieldAlert, MousePointerClick, EyeOff,
  Palette, Rocket, Radar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ProcessSection, { ServicesSection } from "@/components/home/ProcessSection";
import DataSection, { SaferGrowthSection } from "@/components/home/DataSection";
import FinalCta, { HomeFooter } from "@/components/home/FinalCta";
import { openCalendly } from "@/lib/calendly";

export const BOOKING_URL = "https://calendly.com/leprincemaxim/30min";
const CANONICAL_URL = "https://voveron.com/";
const OG_IMAGE = "https://voveron.com/web-app-manifest-512x512.png";

/* ---------------- Reusable ---------------- */
export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ overline, title, sub, align = "left" }) => (
  <Reveal className={`max-w-3xl mb-12 md:mb-16 ${align === "center" ? "mx-auto text-center" : ""}`}>
    {overline && (
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3AED] font-heading mb-4 md:mb-5">{overline}</p>
    )}
    <h2 className="font-heading text-[1.75rem] sm:text-4xl lg:text-[2.75rem] font-bold text-[#0A0A0A] leading-[1.15]">
      {title}
    </h2>
    {sub && <p className="text-[15px] md:text-lg text-[#525252] mt-4 md:mt-5 leading-relaxed">{sub}</p>}
  </Reveal>
);

/* ---------------- Navigation ---------------- */
const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "How We Work", href: "#how-we-work" },
    { label: "Services", href: "#services" },
    { label: "Data", href: "#data" },
    { label: "Blog", href: "/blog" },
  ];

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#FAFAF7]/85 backdrop-blur-md border-b border-[#E7E5E4]" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-3.5 md:py-4 flex items-center justify-between">
          <a href="#" data-testid="logo" className="flex items-center" aria-label="Voveron — Sweepstakes marketing agency, home">
            <img src="/voveron-full-logo.png" alt="Voveron — US sweepstakes marketing agency" className="h-9 md:h-11 w-auto" width="220" height="60" />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) =>
              l.href.startsWith("/") ? (
                <Link key={l.label} to={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors">
                  {l.label}
                </Link>
              ) : (
                <a key={l.label} href={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors">
                  {l.label}
                </a>
              )
            )}
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly}>
              <Button data-testid="nav-cta" className="btn-primary px-5 py-2 text-sm font-semibold rounded-full h-10">
                Book a Strategy Call
              </Button>
            </a>
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(true)} className="md:hidden text-[#0A0A0A]">
            <Menu size={22} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-[#FAFAF7] flex flex-col items-center justify-center gap-8" data-testid="mobile-menu">
          <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} className="absolute top-6 right-6 text-[#0A0A0A]">
            <X size={26} />
          </button>
          {links.map((l) =>
            l.href.startsWith("/") ? (
              <Link key={l.label} to={l.href} onClick={() => setMobileOpen(false)} className="text-2xl font-heading font-semibold text-[#0A0A0A] hover:text-[#7C3AED] transition-colors">
                {l.label}
              </Link>
            ) : (
              <a key={l.label} href={l.href} onClick={() => setMobileOpen(false)} className="text-2xl font-heading font-semibold text-[#0A0A0A] hover:text-[#7C3AED] transition-colors">
                {l.label}
              </a>
            )
          )}
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={(e) => { setMobileOpen(false); openCalendly(e); }}>
            <Button className="btn-primary px-8 py-3 font-semibold rounded-full">Book a Strategy Call</Button>
          </a>
        </div>
      )}
    </>
  );
};

/* ---------------- Hero Dashboard (light) ---------------- */
const HERO_METRICS = [
  { label: "Ad Spend",      value: "$84,200",  delta: "−6%",  up: false },
  { label: "Registrations", value: "12,480",   delta: "+11%", up: true  },
  { label: "FTDs",          value: "1,872",    delta: "+18%", up: true  },
  { label: "Deposits",      value: "$196,400", delta: "+24%", up: true  },
  { label: "LTV",           value: "$104.90",  delta: "+9%",  up: true  },
  { label: "ROAS",          value: "2.33x",    delta: "+0.4", up: true  },
];

const SPARK = [22, 30, 26, 38, 34, 46, 44, 58, 52, 68, 74, 82];

const HeroDashboard = () => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
    className="relative"
    data-testid="hero-dashboard"
  >
    <div className="relative bg-white border border-[#E7E5E4] rounded-2xl p-4 md:p-6 shadow-[0_20px_60px_-30px_rgba(10,10,10,0.15)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-2 h-2 rounded-full bg-[#059669] shrink-0" />
          <span className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-[#737373] font-heading font-semibold truncate">Voveron · Player Value</span>
        </div>
        <span className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#A8A29E] border border-[#E7E5E4] rounded-full px-2 md:px-2.5 py-0.5 shrink-0">30d</span>
      </div>

      {/* Metric grid */}
      <div className="grid grid-cols-3 gap-2 md:gap-3">
        {HERO_METRICS.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 + i * 0.07 }}
            className="border border-[#EAE7E1] bg-[#FAFAF7] rounded-lg p-2.5 md:p-3"
            data-testid={`hero-metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#737373] font-medium mb-1 md:mb-1.5 truncate">{m.label}</p>
            <div className="flex items-baseline justify-between gap-1">
              <p className="font-heading text-sm md:text-lg font-bold text-[#0A0A0A] leading-none tabular-nums">{m.value}</p>
              <span className="text-[9px] md:text-[10px] font-semibold text-[#059669] tabular-nums">{m.delta}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Sparkline card */}
      <div className="mt-3 md:mt-4 border border-[#EAE7E1] bg-[#FAFAF7] rounded-lg p-3 md:p-4">
        <div className="flex items-center justify-between mb-2.5 md:mb-3">
          <div className="min-w-0">
            <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#737373] font-medium">Deposits — 12 weeks</p>
            <p className="font-heading text-xs md:text-sm font-bold text-[#0A0A0A] mt-0.5 tabular-nums">$196,400 net</p>
          </div>
          <span className="inline-flex items-center gap-1 text-[9px] md:text-[10px] font-semibold text-[#059669] bg-[#059669]/10 rounded-full px-2 py-0.5 shrink-0">
            <TrendingUp size={10} /> +24%
          </span>
        </div>
        <div className="h-10 md:h-14 flex items-end gap-1 md:gap-1.5" aria-hidden="true">
          {SPARK.map((h, i) => (
            <motion.div
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ delay: 1 + i * 0.05, duration: 0.5, ease: "easeOut" }}
              className="flex-1 rounded-sm bg-[#7C3AED]/85"
            />
          ))}
        </div>
      </div>
      <p className="text-[9px] md:text-[10px] text-[#A8A29E] mt-2.5 md:mt-3">Illustrative dashboard — sample data</p>
    </div>
  </motion.div>
);

/* ---------- Hero ---------- */
const HeroSection = () => (
  <header className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden bg-[#FAFAF7]">
    <div className="grid-overlay absolute inset-0 opacity-60 pointer-events-none" />
    <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#7C3AED]/[0.04] blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 border border-[#E7E5E4] bg-white rounded-full px-3.5 py-1.5 mb-6 md:mb-7"
          data-testid="hero-overline"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
          <span className="text-[11px] md:text-xs font-medium text-[#525252]">US Sweepstakes Marketing Agency</span>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading text-[2rem] sm:text-5xl lg:text-[3.75rem] font-bold text-[#0A0A0A] leading-[1.08] lg:leading-[1.05]"
          data-testid="hero-headline"
        >
          Sweepstakes marketing agency for paid media, creatives &amp; <span className="text-[#7C3AED]">player acquisition</span>.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.22 }}
          className="text-[15px] md:text-lg text-[#525252] mt-5 md:mt-6 max-w-xl leading-relaxed"
          data-testid="hero-subheadline"
        >
          Voveron helps US sweepstakes operators scale paid media on Meta and TikTok with safer creatives, cleaner tracking, sharper attribution and player-value optimization.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.36 }}
          className="mt-7 md:mt-9 flex flex-wrap items-center gap-5 md:gap-6"
        >
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly} className="w-full sm:w-auto">
            <Button data-testid="hero-primary-cta" className="btn-primary w-full sm:w-auto px-7 py-6 text-base font-semibold rounded-full h-auto">
              Book a Strategy Call <ArrowRight size={18} className="ml-2" />
            </Button>
          </a>
          <a
            href="#how-we-work"
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A0A0A] hover:text-[#7C3AED] transition-colors"
          >
            See how we work
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
      <HeroDashboard />
    </div>
  </header>
);

/* ---------------- Focus Bar ---------------- */
const FOCUS_ITEMS = [
  "US Sweepstakes Marketing",
  "Meta Ads",
  "TikTok Testing",
  "UGC Creatives",
  "Player Acquisition",
  "Tracking & LTV",
];
const FocusBar = () => {
  const track = [...FOCUS_ITEMS, ...FOCUS_ITEMS, ...FOCUS_ITEMS, ...FOCUS_ITEMS];
  return (
    <div className="border-y border-[#E7E5E4] bg-white overflow-hidden focus-marquee" data-testid="focus-bar">
      <div className="focus-marquee-track">
        {track.map((t, i) => (
          <span key={i}>
            {t}
            <em>✦</em>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Problem Section ---------------- */
const PROBLEMS = [
  { icon: GitBranch,          title: "Attribution gaps",                text: "Meta, GA4 and your backend never agree. Nobody knows the real cost per FTD." },
  { icon: UserX,              title: "Low-quality registrations",       text: "Cheap signups that never make a first deposit inflate dashboards and drain budget." },
  { icon: ShieldAlert,        title: "Rejected or limited creatives",   text: "Unsafe win and cash claims get accounts flagged and block scaling at the worst moment." },
  { icon: MousePointerClick,  title: "Optimized for signups, not value",text: "Feed the algorithm registration events and it finds registrants — not depositors." },
  { icon: EyeOff,             title: "No clear view of player value",   text: "Without FTDs, repeat deposits, withdrawals and LTV in one place, scaling is a guess." },
];

const ProblemSection = () => (
  <section className="py-20 md:py-28 bg-[#FAFAF7]" data-testid="problem-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
      <SectionHeading
        overline="The real problem"
        title="Sweepstakes player acquisition breaks on tracking, creative and retention — not traffic."
        sub="Five patterns we see across almost every US sweepstakes ad account before we come in."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {PROBLEMS.map((p, i) => (
          <Reveal key={p.title} delay={(i % 3) * 0.06}>
            <div
              className="h-full bg-white border border-[#E7E5E4] rounded-2xl p-6 md:p-7 hover:border-[#0A0A0A]/20 hover:shadow-[0_10px_30px_-15px_rgba(10,10,10,0.15)] transition-all duration-300"
              data-testid={`problem-card-${i + 1}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#7C3AED]/8 border border-[#7C3AED]/15 flex items-center justify-center text-[#7C3AED] mb-5">
                <p.icon size={20} strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2.5">{p.title}</h3>
              <p className="text-[15px] text-[#525252] leading-relaxed">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Solution Section ---------------- */
const SOLUTIONS = [
  { icon: Palette, title: "Safer Creative Engine", text: "UGC and static concepts built for the sweepstakes category — free-to-play positioning, compliant claims, and every asset reviewed before it goes live." },
  { icon: Rocket,  title: "Paid Media Execution",  text: "Meta-first media buying with structured TikTok testing, disciplined budgets and clear scaling rules — managed like a system, not a set of campaigns." },
  { icon: Radar,   title: "Player-Level Tracking", text: "Pixel, CAPI and postbacks wired to your backend, so campaigns optimize on FTDs and deposit value instead of empty signups." },
];

const SolutionSection = () => (
  <section className="py-20 md:py-28 bg-white border-y border-[#E7E5E4]" data-testid="solution-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
      <SectionHeading
        overline="What we build"
        title="A sweepstakes acquisition system — not just ad campaigns."
        sub="Creative, paid media and tracking working as one loop — each part feeds the next."
      />
      <div className="grid md:grid-cols-3 gap-4 md:gap-5">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08}>
            <div
              className="h-full bg-[#FAFAF7] border border-[#E7E5E4] rounded-2xl p-6 md:p-8 hover:border-[#7C3AED]/40 hover:bg-white transition-all duration-300"
              data-testid={`solution-card-${i + 1}`}
            >
              <div className="w-12 h-12 rounded-xl bg-white border border-[#E7E5E4] flex items-center justify-center text-[#7C3AED] mb-5 md:mb-6 shadow-sm">
                <s.icon size={22} strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg md:text-xl font-bold text-[#0A0A0A] mb-3">{s.title}</h3>
              <p className="text-[15px] text-[#525252] leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Sticky Mobile CTA ---------------- */
const MobileStickyCta = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-testid="mobile-sticky-cta"
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-[#FAFAF7] via-[#FAFAF7]/95 to-[#FAFAF7]/0 transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 pointer-events-none"
      }`}
    >
      <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly} className="block">
        <Button className="btn-primary w-full h-12 text-sm font-semibold rounded-full shadow-[0_10px_30px_-10px_rgba(10,10,10,0.4)]">
          Book a Strategy Call <ArrowRight size={16} className="ml-2" />
        </Button>
      </a>
    </div>
  );
};

/* ---------------- Page ---------------- */
export default function Home() {
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/` : CANONICAL_URL;
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0A]" data-testid="home-page">
      <Helmet>
        <title>Voveron | Sweepstakes Marketing Agency for Paid Media &amp; Player Acquisition</title>
        <meta name="description" content="Voveron helps US sweepstakes operators scale paid media with safer creatives, tracking, attribution and player-value optimization." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Voveron | Sweepstakes Marketing Agency for Paid Media & Player Acquisition" />
        <meta property="og:description" content="Voveron helps US sweepstakes operators scale paid media with safer creatives, tracking, attribution and player-value optimization." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Navigation />
      <HeroSection />
      <FocusBar />
      <ProblemSection />
      <SolutionSection />
      <ProcessSection />
      <ServicesSection />
      <DataSection />
      <SaferGrowthSection />
      <FinalCta />
      <HomeFooter />
      <MobileStickyCta />
    </div>
  );
}
